/**
 * PostHog — mirrors the same event schema as Google Tag Manager (dataLayer) for comparable analytics.
 * Initialized from AnalyticsProvider (GTMProvider); events arrive via gtmService.push().
 */

import type { PostHog } from 'posthog-js';
import type { GTMEvent } from '../types/gtm';
import { getEnvironmentConfig } from '../config/environment';

export function resolvePostHogKey(): string | null {
  const config = getEnvironmentConfig();
  if (config.name === 'production') {
    return import.meta.env.VITE_POSTHOG_KEY || null;
  }
  if (config.name === 'staging') {
    return import.meta.env.VITE_POSTHOG_KEY_STAGING || import.meta.env.VITE_POSTHOG_KEY || null;
  }
  return (
    import.meta.env.VITE_POSTHOG_KEY_LOCAL ||
    import.meta.env.VITE_POSTHOG_KEY_STAGING ||
    import.meta.env.VITE_POSTHOG_KEY ||
    null
  );
}

export function resolvePostHogHost(): string {
  return import.meta.env.VITE_POSTHOG_HOST || 'https://e.virtoly.com';
}

function isAutocaptureEnabled(): boolean {
  return import.meta.env.VITE_POSTHOG_ENABLE_AUTOCAPTURE === 'true';
}

function isSessionRecordingDisabled(): boolean {
  return import.meta.env.VITE_POSTHOG_DISABLE_SESSION_RECORDING === 'true';
}

class PostHogAnalyticsService {
  private ready = false;
  private posthog: PostHog | null = null;
  /** Last URL we sent a $pageview for — used to emit $pageleave on SPA navigations. */
  private lastPageLocation: string | null = null;
  private lastPagePath: string | null = null;

  init(apiKey: string | null): void {
    if (!apiKey || typeof window === 'undefined' || this.ready) {
      return;
    }

    const envConfig = getEnvironmentConfig();

    void import('posthog-js')
      .then(({ default: posthogClient }) => {
        posthogClient.init(apiKey, {
          api_host: resolvePostHogHost(),
          defaults: '2026-01-30',
          capture_pageview: false,
          // With manual $pageview, default `if_capture_pageview` would never send $pageleave.
          capture_pageleave: true,
          persistence: 'localStorage+cookie',
          autocapture: isAutocaptureEnabled(),
          disable_session_recording: isSessionRecordingDisabled(),
          disable_surveys: true,
        });

        posthogClient.register({
          app_environment: envConfig.name,
        });

        if (envConfig.debug && import.meta.env.VITE_POSTHOG_DEBUG === 'true') {
          posthogClient.debug();
        }

        this.posthog = posthogClient;
        this.ready = true;
      })
      .catch((error) => {
        console.error('[PostHog] init failed:', error);
      });
  }

  isReady(): boolean {
    return this.ready;
  }

  /**
   * Maps dataLayer-shaped events (same names/properties as GTM) into PostHog captures + identify/reset.
   */
  forwardGtmEvent(event: GTMEvent | Record<string, unknown>): void {
    const posthog = this.posthog;
    if (!this.ready || !posthog || typeof window === 'undefined') {
      return;
    }

    try {
      const e = event as Record<string, unknown>;
      const eventName = e.event;
      if (typeof eventName !== 'string') {
        return;
      }

      if (eventName === 'set_user_properties') {
        const userId = e.userId;
        const userType = e.userType;
        const email = e.email;
        const rest = { ...e };
        delete rest.event;
        delete rest.userId;
        delete rest.userType;
        delete rest.email;
        if (userId != null && userId !== '') {
          posthog.identify(String(userId), {
            user_type: userType,
            email,
            ...rest,
          });
        }
        return;
      }

      if (eventName === 'clear_user_properties') {
        posthog.reset();
        return;
      }

      if (eventName === 'page_view') {
        const pageLocation = (e.page_location as string) || window.location.href;
        const pagePath = (e.page_path as string) || window.location.pathname;
        if (
          this.lastPageLocation !== null &&
          this.lastPageLocation !== pageLocation
        ) {
          posthog.capture('$pageleave', {
            $current_url: this.lastPageLocation,
            ...(this.lastPagePath ? { $pathname: this.lastPagePath } : {}),
          });
        }
        this.lastPageLocation = pageLocation;
        this.lastPagePath = pagePath;
        posthog.capture('$pageview', {
          $current_url: pageLocation,
          $pathname: pagePath,
          page_title: e.page_title as string | undefined,
        });
        return;
      }

      const props = { ...e };
      delete props.event;
      posthog.capture(eventName, props as Record<string, unknown>);
    } catch (error) {
      console.error('[PostHog] Error forwarding event:', error);
    }
  }
}

export const posthogService = new PostHogAnalyticsService();
